import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { formatDate, type BlogPost } from '../../lib/blog';
import BlogThumb from './BlogThumb';

interface BlogFeaturedCardProps {
  post: BlogPost;
  lang: 'fr' | 'en';
}

function formatTagLabel(tag: string): string {
  if (!tag) return tag;
  return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
}

const BlogFeaturedCard: React.FC<BlogFeaturedCardProps> = ({ post, lang }) => {
  const { t } = useI18n();

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_-12px_rgba(15,23,42,0.12)] transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-[0_16px_40px_-16px_rgba(37,99,235,0.28)] sm:rounded-3xl lg:flex-row"
    >
      <div className="flex min-w-0 flex-1">
        <BlogThumb brand size="side" className="w-14 sm:w-20 lg:w-24" />

        <div className="flex min-w-0 flex-1 flex-col justify-center p-4 sm:p-8 lg:p-10">
          <div className="mb-3 hidden flex-wrap gap-1.5 sm:mb-4 sm:flex sm:gap-2">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 sm:px-2.5 sm:py-1 sm:text-[11px]"
              >
                {formatTagLabel(tag)}
              </span>
            ))}
          </div>
          <h2 className="mb-2 text-lg font-bold leading-snug tracking-tight text-slate-900 sm:mb-3 sm:text-2xl lg:text-[1.65rem]">
            {post.title}
          </h2>
          <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-slate-500 sm:mb-5 sm:text-base">
            {post.description}
          </p>
          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400 sm:mb-5 sm:gap-x-4">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(post.date, lang)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {t('blog.readingTime', { minutes: post.readingTime })}
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 transition-colors duration-200 group-hover:text-blue-700">
            <span className="underline decoration-transparent underline-offset-[5px] decoration-2 transition-[text-decoration-color] duration-200 group-hover:decoration-blue-600/70">
              {t('blog.readArticle')}
            </span>
            <span
              aria-hidden
              className="inline-block translate-x-0 transition-transform duration-200 group-hover:translate-x-1"
            >
              →
            </span>
          </span>
        </div>
      </div>

      <div className="hidden border-t border-slate-100 p-3 sm:p-5 lg:flex lg:w-[min(52%,28rem)] lg:shrink-0 lg:items-center lg:border-l lg:border-t-0 lg:p-6 xl:w-1/2">
        <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 shadow-sm sm:rounded-2xl">
          <img
            src="/images/blog/tableau-prospection-leads.jpg"
            alt=""
            className="block h-auto w-full"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </Link>
  );
};

export default BlogFeaturedCard;
